import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Briefcase, FileText, CreditCard, Users, Award, Save, X, Edit2, Calendar, MapPin, Building2, User, Users2, FileCheck, Info, Plus, Trash2 } from 'lucide-react';
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { getEmployeeDropdownOptions, getEmployeesForDropdown } from "@/services/employee.service";
import { jobTitleService } from "@/services/jobTitle.service";
import { useAuthStore } from "@/stores/authStore";

// Type definitions for job information
interface JobInfo {
  effectiveDate: string;
  location: string;
  jobTitle: string;
  category: string;
  reportingManager: string | string[] | { _id: string; firstName: string; lastName: string; employeeId: string }[];
  lineManager: string | string[] | { _id: string; firstName: string; lastName: string; employeeId: string }[];
  lineManager2: string | string[] | { _id: string; firstName: string; lastName: string; employeeId: string }[];
  employmentStatus: string;
  probationPeriod: string;
  probationEndDate: string;
  feedbackRemainderDays: string;
  note: string;
}

interface AgreementInfo {
  startDate: string;
  endDate: string;
  periodInMonths: string;
  notifyPeriodInDays: string;
  note: string;
}

interface JobSectionProps {
  jobData: JobInfo;
  setJobData: React.Dispatch<React.SetStateAction<{
    effectiveDate: string;
    location: string;
    jobTitle: string;
    category: string;
    reportingManager: string;
    lineManager: string;
    lineManager2: string;
    employmentStatus: string;
    probationPeriod: string;
    probationEndDate: string;
    feedbackRemainderDays: string;
    note: string;
  }>>;
  isEditing: boolean;
}

interface AgreementSectionProps {
  agreementData: AgreementInfo[];
  setAgreementData: React.Dispatch<React.SetStateAction<AgreementInfo[]>>;
  isEditing: boolean;
}

interface CompensationInfo {
  startDate: string;
  endDate: string;
  periodInMonths: string;
  previousSalary: string;
  incrementPercentage: string;
  newSalary: string;
  payFrequency: string;
  noticePeriodInDays: string;
  notifyPeriodInDays: string;
  monthlyBonus: string;
  salaryMode: string;
  note: string;
}

interface CompensationSectionProps {
  compensationData: CompensationInfo[];
  setCompensationData: React.Dispatch<React.SetStateAction<CompensationInfo[]>>;
  isEditing: boolean;
}

interface ResignationInfo {
  resignationType: string;
  reason: string;
  noticeGivenOn: string;
  lastWorkingDay: string;
  note: string;
  revokeReason: string;
  remark: string;
  revokedOn: string;
  status: string;
}

interface ResignationSectionProps {
  resignationData: ResignationInfo;
  setResignationData: React.Dispatch<React.SetStateAction<ResignationInfo>>;
  isEditing: boolean;
  employeeData?: any;
}

interface InternshipInfo {
  startDate: string;
  endDate: string;
  length: string;
  reduceExtended: string;
}

interface InternshipSectionProps {
  internshipData: InternshipInfo[];
  setInternshipData: React.Dispatch<React.SetStateAction<InternshipInfo[]>>;
  isEditing: boolean;
}

// Helper function to safely convert any value to string
const safeStringify = (value: any): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return value.toString();
  if (Array.isArray(value)) {
    return value.map(item => safeStringify(item)).filter(Boolean).join(', ');
  }
  if (typeof value === 'object') {
    return value.title || value.name || value.description || JSON.stringify(value);
  }
  return String(value);
};

// Helper function to format dates in human-readable format
const formatDate = (dateValue: any): string => {
  if (!dateValue) return '';
  
  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return safeStringify(dateValue);
    
    // Format as DD/MM/YYYY
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (error) {
    return safeStringify(dateValue);
  }
};
const resolveManagerNames = (managers: any, employeesList: any[]): string => {
  if (!managers) {
    return '';
  }

  const managerArray = Array.isArray(managers) ? managers : [managers];
  
  if (managerArray.length === 0) {
    return '';
  }

  const resolvedNames = managerArray.map((manager: any) => {
    // If it's already a populated object with firstName (from backend population)
    if (typeof manager === 'object' && manager.firstName) {
      const fullName = `${manager.firstName} ${manager.lastName || ''}`.trim();
      return fullName;
    }
    
    // If it's an ID string, try to find in the employees list
    if (typeof manager === 'string' && manager.trim() !== '') {
      const foundManager = employeesList.find((emp: any) => 
        emp.id === manager || 
        emp._id === manager ||
        emp.employeeId === manager
      );
      
      if (foundManager && foundManager.name) {
        return foundManager.name;
      }
      
      return manager; // Return original if not found
    }
    
    return 'Unknown';
  }).filter(name => name !== 'Unknown');

  return resolvedNames.length > 0 ? resolvedNames.join(', ') : '';
};

// Helper function to resolve job title name from ID
const resolveJobTitleName = (jobTitleId: string, jobTitles: Array<{ id: string; title: string; description: string }>): string => {
  if (!jobTitleId || !Array.isArray(jobTitles)) return jobTitleId || '';
  
  const jobTitle = jobTitles.find(jt => jt.id === jobTitleId);
  return jobTitle ? jobTitle.title : jobTitleId;
};

// Job Information Section
export const JobInfoSection = ({ jobData, setJobData }: JobSectionProps) => {
  const { hasPermission } = useAuthStore();
  
  // Check if user has permission to edit job information
  const canEditJob = hasPermission('EMPLOYEES_EDIT') || hasPermission('EMPLOYEES_CREATE');
  
  const [localIsEditing, setLocalIsEditing] = React.useState(false);
  const [localData, setLocalData] = React.useState<{
    effectiveDate: string;
    location: string;
    jobTitle: string;
    category: string;
    reportingManager: string;
    lineManager: string;
    lineManager2: string;
    employmentStatus: string;
    probationPeriod: string;
    probationEndDate: string;
    feedbackRemainderDays: string;
    note: string;
  }>({
    effectiveDate: '',
    location: '',
    jobTitle: '',
    category: '',
    reportingManager: '',
    lineManager: '',
    lineManager2: '',
    employmentStatus: '',
    probationPeriod: '',
    probationEndDate: '',
    feedbackRemainderDays: '',
    note: ''
  });
  const [dropdownOptions, setDropdownOptions] = React.useState<{
    locations: string[];
  }>({
    locations: []
  });
  const [_employeesList, setEmployeesList] = React.useState<any[]>([]);
  const [jobTitles, setJobTitles] = React.useState<Array<{ id: string; title: string; description: string }>>([]);
  const [managerNames, setManagerNames] = React.useState({
    reportingManager: '',
    lineManager: '',
    lineManager2: ''
  });

  React.useEffect(() => {
    // Ensure all fields are strings, not objects
    const processedJobData: {
      effectiveDate: string;
      location: string;
      jobTitle: string;
      category: string;
      reportingManager: string;
      lineManager: string;
      lineManager2: string;
      employmentStatus: string;
      probationPeriod: string;
      probationEndDate: string;
      feedbackRemainderDays: string;
      note: string;
    } = jobData ? {
      effectiveDate: safeStringify(jobData.effectiveDate),
      location: safeStringify(jobData.location),
      jobTitle: jobData.jobTitle ? (typeof jobData.jobTitle === 'string' ? jobData.jobTitle : (jobData.jobTitle as any)?._id || (jobData.jobTitle as any)?.id || '') : '',
      category: safeStringify(jobData.category),
      reportingManager: jobData.reportingManager ? (typeof jobData.reportingManager === 'string' ? jobData.reportingManager : Array.isArray(jobData.reportingManager) ? jobData.reportingManager.join(', ') : '') : '',
      lineManager: jobData.lineManager ? (typeof jobData.lineManager === 'string' ? jobData.lineManager : Array.isArray(jobData.lineManager) ? jobData.lineManager.join(', ') : '') : '',
      lineManager2: jobData.lineManager2 ? (typeof jobData.lineManager2 === 'string' ? jobData.lineManager2 : Array.isArray(jobData.lineManager2) ? jobData.lineManager2.join(', ') : '') : '',
      employmentStatus: safeStringify(jobData.employmentStatus),
      probationPeriod: safeStringify(jobData.probationPeriod),
      probationEndDate: safeStringify(jobData.probationEndDate),
      feedbackRemainderDays: safeStringify(jobData.feedbackRemainderDays),
      note: safeStringify(jobData.note)
    } : {
      effectiveDate: '',
      location: '',
      jobTitle: '',
      category: '',
      reportingManager: '',
      lineManager: '',
      lineManager2: '',
      employmentStatus: '',
      probationPeriod: '',
      probationEndDate: '',
      feedbackRemainderDays: '',
      note: ''
    };
    
    setLocalData(processedJobData);
  }, [jobData]);

  // Fetch dropdown options and employees from backend
  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const [options, employees, jobTitlesData] = await Promise.all([
          getEmployeeDropdownOptions(),
          getEmployeesForDropdown(),
          jobTitleService.getJobTitlesForDropdown()
        ]);
        
        setDropdownOptions(options);
        setEmployeesList(employees);
        setJobTitles(jobTitlesData);
        
        // Resolve manager names
        if (jobData) {
          setManagerNames({
            reportingManager: resolveManagerNames(jobData.reportingManager, employees),
            lineManager: resolveManagerNames(jobData.lineManager, employees),
            lineManager2: resolveManagerNames(jobData.lineManager2, employees)
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        // Fallback to default options
        setDropdownOptions({
          locations: ['Surat', 'Ahmedabad', 'Mumbai', 'Bangalore', 'Delhi', 'Chennai', 'Hyderabad', 'Pune']
        });
      }
    };

    fetchData();
  }, [jobData]);

  const handleSave = () => {
    // Merge localData with original jobData to preserve existing values
    const mergedData = {
      joiningDate: localData.effectiveDate || (typeof jobData?.effectiveDate === 'string' ? jobData.effectiveDate : '') || '',
      location: localData.location || (typeof jobData?.location === 'string' ? jobData.location : '') || '',
      jobTitle: localData.jobTitle || (typeof jobData?.jobTitle === 'string' ? jobData.jobTitle : '') || '',
      category: localData.category || (typeof jobData?.category === 'string' ? jobData.category : '') || '',
      // Convert manager strings to arrays for backend
      reportingManager: localData.reportingManager ? localData.reportingManager.split(',').filter(id => id.trim()) : (Array.isArray(jobData?.reportingManager) ? jobData.reportingManager : []),
      lineManager: localData.lineManager ? localData.lineManager.split(',').filter(id => id.trim()) : (Array.isArray(jobData?.lineManager) ? jobData.lineManager : []),
      lineManager2: localData.lineManager2 ? localData.lineManager2.split(',').filter(id => id.trim()) : (Array.isArray(jobData?.lineManager2) ? jobData.lineManager2 : []),
      employmentStatus: localData.employmentStatus || (typeof jobData?.employmentStatus === 'string' ? jobData.employmentStatus : '') || '',
      probationPeriod: localData.probationPeriod || (typeof jobData?.probationPeriod === 'string' ? jobData.probationPeriod : '') || '',
      probationEndDate: localData.probationEndDate || (typeof jobData?.probationEndDate === 'string' ? jobData.probationEndDate : '') || '',
      feedbackRemainderDays: localData.feedbackRemainderDays || (typeof jobData?.feedbackRemainderDays === 'string' ? jobData.feedbackRemainderDays : '') || '',
      note: localData.note || (typeof jobData?.note === 'string' ? jobData.note : '') || ''
    };
    
    // Convert arrays back to strings for localData compatibility
    const finalData = {
      effectiveDate: mergedData.joiningDate,
      location: mergedData.location,
      jobTitle: mergedData.jobTitle,
      category: mergedData.category,
      reportingManager: Array.isArray(mergedData.reportingManager) ? mergedData.reportingManager.join(',') : (typeof mergedData.reportingManager === 'string' ? mergedData.reportingManager : ''),
      lineManager: Array.isArray(mergedData.lineManager) ? mergedData.lineManager.join(',') : (typeof mergedData.lineManager === 'string' ? mergedData.lineManager : ''),
      lineManager2: Array.isArray(mergedData.lineManager2) ? mergedData.lineManager2.join(',') : (typeof mergedData.lineManager2 === 'string' ? mergedData.lineManager2 : ''),
      employmentStatus: mergedData.employmentStatus,
      probationPeriod: mergedData.probationPeriod,
      probationEndDate: mergedData.probationEndDate,
      feedbackRemainderDays: mergedData.feedbackRemainderDays,
      note: mergedData.note
    };
    
    setJobData(finalData);
    setLocalIsEditing(false);
    toast.success('Job information updated successfully');
  };

  const handleCancel = () => {
    // Reset to processed data
    const processedJobData: {
      effectiveDate: string;
      location: string;
      jobTitle: string;
      category: string;
      reportingManager: string;
      lineManager: string;
      lineManager2: string;
      employmentStatus: string;
      probationPeriod: string;
      probationEndDate: string;
      feedbackRemainderDays: string;
      note: string;
    } = jobData ? {
      effectiveDate: safeStringify(jobData.effectiveDate),
      location: safeStringify(jobData.location),
      jobTitle: jobData.jobTitle ? (typeof jobData.jobTitle === 'string' ? jobData.jobTitle : (jobData.jobTitle as any)?._id || (jobData.jobTitle as any)?.id || '') : '',
      category: safeStringify(jobData.category),
      reportingManager: jobData.reportingManager ? (typeof jobData.reportingManager === 'string' ? jobData.reportingManager : Array.isArray(jobData.reportingManager) ? jobData.reportingManager.join(', ') : '') : '',
      lineManager: jobData.lineManager ? (typeof jobData.lineManager === 'string' ? jobData.lineManager : Array.isArray(jobData.lineManager) ? jobData.lineManager.join(', ') : '') : '',
      lineManager2: jobData.lineManager2 ? (typeof jobData.lineManager2 === 'string' ? jobData.lineManager2 : Array.isArray(jobData.lineManager2) ? jobData.lineManager2.join(', ') : '') : '',
      employmentStatus: safeStringify(jobData.employmentStatus),
      probationPeriod: safeStringify(jobData.probationPeriod),
      probationEndDate: safeStringify(jobData.probationEndDate),
      feedbackRemainderDays: safeStringify(jobData.feedbackRemainderDays),
      note: safeStringify(jobData.note)
    } : {
      effectiveDate: '',
      location: '',
      jobTitle: '',
      category: '',
      reportingManager: '',
      lineManager: '',
      lineManager2: '',
      employmentStatus: '',
      probationPeriod: '',
      probationEndDate: '',
      feedbackRemainderDays: '',
      note: ''
    };
    
    setLocalData(processedJobData);
    setLocalIsEditing(false);
    toast.info('Edit cancelled');
  };

  const handleEdit = () => {
    setLocalIsEditing(true);
    toast.info('Edit mode enabled');
  };

  // Helper function to calculate probation end date
  const calculateProbationEndDate = (effectiveDate: string, probationPeriod: string): string => {
    if (!effectiveDate || !probationPeriod) {
      return '';
    }

    const startDate = new Date(effectiveDate);
    const months = parseInt(probationPeriod, 10);
    
    if (isNaN(months)) {
      return '';
    }

    const endDate = new Date(startDate);
    endDate.setMonth(endDate.getMonth() + months);
    
    return endDate.toISOString().split('T')[0]; // Return in YYYY-MM-DD format
  };

  const updateField = (field: keyof JobInfo, value: string) => {
    const newData = {
      ...localData,
      [field]: value
    };

    // Auto-calculate probation end date if employment status is "Probation" and probation period is provided
    if (field === 'employmentStatus' && value === 'Probation' && localData.probationPeriod && localData.effectiveDate) {
      newData.probationEndDate = calculateProbationEndDate(localData.effectiveDate, localData.probationPeriod);
    } else if (field === 'probationPeriod' && localData.employmentStatus === 'Probation' && localData.effectiveDate) {
      newData.probationEndDate = calculateProbationEndDate(localData.effectiveDate, value);
    } else if (field === 'effectiveDate' && localData.employmentStatus === 'Probation' && localData.probationPeriod) {
      newData.probationEndDate = calculateProbationEndDate(value, localData.probationPeriod);
    }

    setLocalData(newData);
  };

  return (
    <Card className="border border-gray-200 shadow-sm bg-white rounded-lg">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <Briefcase className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-base font-semibold text-gray-900">
                Job Information
              </CardTitle>
             </div>
          </div>
          <div className="flex items-center gap-1">
            {canEditJob && localIsEditing && (
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                className="gap-1 bg-blue-600 hover:bg-blue-700 text-xs px-3 py-1"
              >
                <Save className="h-3 w-3" />
                Save
              </Button>
            )}
            {canEditJob && (
            <Button
              variant="outline"
              size="sm"
              onClick={localIsEditing ? handleCancel : handleEdit}
              className="gap-1 hover:bg-gray-50 text-xs px-3 py-1"
            >
              {localIsEditing ? (
                <>
                  <X className="h-3 w-3" />
                  Cancel
                </>
              ) : (
                <>
                  <Edit2 className="h-3 w-3" />
                  Edit
                </>
              )}
            </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {localIsEditing ? (
          <>
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-blue-50 rounded-lg flex items-center justify-center">
                    <Briefcase className="h-3 w-3 text-blue-600" />
                  </div>
                  <h5 className="font-medium text-sm text-gray-700">
                    Job Details
                  </h5>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">
                    Effective Date <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="date"
                    value={localData.effectiveDate}
                    onChange={(e) => updateField('effectiveDate', e.target.value)}
                    className="h-8 text-xs"
                    required
                  />
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">
                    Location
                  </Label>
                  <Select 
                    value={localData.location || ''} 
                    onValueChange={(value) => updateField('location', value === 'clear' ? '' : value)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="clear">
                        <span className="text-gray-500 italic">Clear selection</span>
                      </SelectItem>
                      {dropdownOptions.locations.map((location) => (
                        <SelectItem key={location} value={location}>
                          {location}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">
                    Job Title <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={localData.jobTitle}
                    onValueChange={(value) => updateField('jobTitle', value)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select job title" />
                    </SelectTrigger>
                    <SelectContent>
                      {jobTitles.length > 0 ? (
                        jobTitles.map((jobTitle) => (
                          <SelectItem key={jobTitle.id} value={jobTitle.id}>
                            {jobTitle.title}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="" disabled>
                          No job titles available
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">
                    Category
                  </Label>
                  <Input
                    value={localData.category}
                    onChange={(e) => updateField('category', e.target.value)}
                    placeholder="Enter category"
                    className="h-8 text-xs"
                  />
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">
                    Reporting Manager
                  </Label>
                  <Select
                    value=""
                    onValueChange={(employeeId) => {
                      if (employeeId && !localData.reportingManager.includes(employeeId)) {
                        const currentManagers = localData.reportingManager ? localData.reportingManager.split(',').filter(id => id.trim()) : [];
                        updateField('reportingManager', [...currentManagers, employeeId].join(','));
                      }
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select reporting manager" />
                    </SelectTrigger>
                    <SelectContent>
                      {_employeesList
                        .filter(emp => !localData.reportingManager?.includes(emp._id || emp.id || ''))
                        .map((employee) => (
                          <SelectItem key={employee._id || employee.id} value={employee._id || employee.id || ''}>
                            {employee.name} ({employee.employeeId})
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {localData.reportingManager && (
                    <div className="flex flex-wrap gap-1">
                      {localData.reportingManager.split(',').filter(id => id.trim()).map((managerId) => {
                        const employee = _employeesList.find(emp => (emp._id || emp.id) === managerId.trim());
                        return employee ? (
                          <div key={managerId} className="flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-xs">
                            <span>{employee.name} ({employee.employeeId})</span>
                            <button
                              type="button"
                              onClick={() => {
                                const currentManagers = localData.reportingManager.split(',').filter(id => id.trim() !== managerId.trim());
                                updateField('reportingManager', currentManagers.join(','));
                              }}
                              className="ml-1 text-blue-600 hover:text-blue-800"
                            >
                              ×
                            </button>
                          </div>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">
                    Line Manager
                  </Label>
                  <Select
                    value=""
                    onValueChange={(employeeId) => {
                      if (employeeId && !localData.lineManager.includes(employeeId)) {
                        const currentManagers = localData.lineManager ? localData.lineManager.split(',').filter(id => id.trim()) : [];
                        updateField('lineManager', [...currentManagers, employeeId].join(','));
                      }
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select line manager" />
                    </SelectTrigger>
                    <SelectContent>
                      {_employeesList
                        .filter(emp => !localData.lineManager?.includes(emp._id || emp.id || ''))
                        .map((employee) => (
                          <SelectItem key={employee._id || employee.id} value={employee._id || employee.id || ''}>
                            {employee.name} ({employee.employeeId})
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {localData.lineManager && (
                    <div className="flex flex-wrap gap-1">
                      {localData.lineManager.split(',').filter(id => id.trim()).map((managerId) => {
                        const employee = _employeesList.find(emp => (emp._id || emp.id) === managerId.trim());
                        return employee ? (
                          <div key={managerId} className="flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-xs">
                            <span>{employee.name} ({employee.employeeId})</span>
                            <button
                              type="button"
                              onClick={() => {
                                const currentManagers = localData.lineManager.split(',').filter(id => id.trim() !== managerId.trim());
                                updateField('lineManager', currentManagers.join(','));
                              }}
                              className="ml-1 text-blue-600 hover:text-blue-800"
                            >
                              ×
                            </button>
                          </div>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">
                    Line Manager 2
                  </Label>
                  <Select
                    value=""
                    onValueChange={(employeeId) => {
                      if (employeeId && !localData.lineManager2.includes(employeeId)) {
                        const currentManagers = localData.lineManager2 ? localData.lineManager2.split(',').filter(id => id.trim()) : [];
                        updateField('lineManager2', [...currentManagers, employeeId].join(','));
                      }
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select line manager 2" />
                    </SelectTrigger>
                    <SelectContent>
                      {_employeesList
                        .filter(emp => !localData.lineManager2?.includes(emp._id || emp.id || ''))
                        .map((employee) => (
                          <SelectItem key={employee._id || employee.id} value={employee._id || employee.id || ''}>
                            {employee.name} ({employee.employeeId})
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {localData.lineManager2 && (
                    <div className="flex flex-wrap gap-1">
                      {localData.lineManager2.split(',').filter(id => id.trim()).map((managerId) => {
                        const employee = _employeesList.find(emp => (emp._id || emp.id) === managerId.trim());
                        return employee ? (
                          <div key={managerId} className="flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-xs">
                            <span>{employee.name} ({employee.employeeId})</span>
                            <button
                              type="button"
                              onClick={() => {
                                const currentManagers = localData.lineManager2.split(',').filter(id => id.trim() !== managerId.trim());
                                updateField('lineManager2', currentManagers.join(','));
                              }}
                              className="ml-1 text-blue-600 hover:text-blue-800"
                            >
                              ×
                            </button>
                          </div>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">
                    Employment Status
                  </Label>
                  <Select 
                    value={localData.employmentStatus || ''} 
                    onValueChange={(value) => updateField('employmentStatus', value === 'clear' ? '' : value)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select employment status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="clear">
                        <span className="text-gray-500 italic">Clear selection</span>
                      </SelectItem>
                      <SelectItem value="Full-time">Full-time</SelectItem>
                      <SelectItem value="Part-time">Part-time</SelectItem>
                      <SelectItem value="Probation">Probation</SelectItem>
                      <SelectItem value="Contract">Contract</SelectItem>
                      <SelectItem value="Work from Home">Work from Home</SelectItem>
                      <SelectItem value="Intern">Intern</SelectItem>
                      <SelectItem value="Terminated">Terminated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">
                    Probation Period (Months)
                  </Label>
                  <Input
                    type="number"
                    value={localData.probationPeriod}
                    onChange={(e) => updateField('probationPeriod', e.target.value)}
                    placeholder="Enter probation period in months"
                    className="h-8 text-xs"
                  />
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">
                    Probation End Date
                  </Label>
                  <Input
                    type="date"
                    value={localData.probationEndDate}
                    onChange={(e) => updateField('probationEndDate', e.target.value)}
                    className="h-8 text-xs"
                  />
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">
                    Feedback Remainder Days
                  </Label>
                  <Input
                    type="number"
                    value={localData.feedbackRemainderDays}
                    onChange={(e) => updateField('feedbackRemainderDays', e.target.value)}
                    placeholder="Enter feedback remainder days"
                    className="h-8 text-xs"
                  />
                </div>
                
                <div className="space-y-1 md:col-span-2">
                  <Label className="text-xs font-medium text-gray-700">
                    Note
                  </Label>
                  <Input
                    value={localData.note}
                    onChange={(e) => updateField('note', e.target.value)}
                    placeholder="Enter additional notes"
                    className="h-8 text-xs"
                  />
                </div>
              </div>
            </div>
          </>
          ) : (
            <div className="space-y-3">
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-blue-50 rounded-lg flex items-center justify-center">
                        <Briefcase className="h-3 w-3 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-medium text-sm text-gray-900">
                          {resolveJobTitleName(localData.jobTitle, jobTitles) || 'Job Title'}
                        </div>
                       
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1 border-t border-gray-200">
                    {localData.effectiveDate && (
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3 text-blue-600" />
                        <span className="text-xs text-gray-600">Effective: {formatDate(localData.effectiveDate)}</span>
                      </div>
                    )}
                    {localData.location && (
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3 text-blue-600" />
                        <span className="text-xs text-gray-600">Location: {safeStringify(localData.location)}</span>
                      </div>
                    )}
                    {localData.category && (
                      <div className="flex items-center gap-1">
                        <Building2 className="h-3 w-3 text-blue-600" />
                        <span className="text-xs text-gray-600">Category: {safeStringify(localData.category)}</span>
                      </div>
                    )}
                    {managerNames.reportingManager && (
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3 text-blue-600" />
                        <span className="text-xs text-gray-600">RM: {managerNames.reportingManager}</span>
                      </div>
                    )}
                    {managerNames.lineManager && (
                      <div className="flex items-center gap-1">
                        <Users2 className="h-3 w-3 text-blue-600" />
                        <span className="text-xs text-gray-600">LM: {managerNames.lineManager}</span>
                      </div>
                    )}
                    {managerNames.lineManager2 && (
                      <div className="flex items-center gap-1">
                        <Users2 className="h-3 w-3 text-blue-600" />
                        <span className="text-xs text-gray-600">LM2: {managerNames.lineManager2}</span>
                      </div>
                    )}
                    {localData.employmentStatus && (
                      <div className="flex items-center gap-1">
                        <Award className="h-3 w-3 text-blue-600" />
                        <span className="text-xs text-gray-600">Status: {safeStringify(localData.employmentStatus)}</span>
                      </div>
                    )}
                    {localData.probationPeriod && (
                      <div className="flex items-center gap-1">
                        <Award className="h-3 w-3 text-blue-600" />
                        <span className="text-xs text-gray-600">Probation: {safeStringify(localData.probationPeriod)} months</span>
                      </div>
                    )}
                    {localData.probationEndDate && (
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3 text-blue-600" />
                        <span className="text-xs text-gray-600">Probation End: {formatDate(localData.probationEndDate)}</span>
                      </div>
                    )}
                    {localData.feedbackRemainderDays && (
                      <div className="flex items-center gap-1">
                        <Award className="h-3 w-3 text-blue-600" />
                        <span className="text-xs text-gray-600">Feedback Reminder: {safeStringify(localData.feedbackRemainderDays)} days</span>
                      </div>
                    )}
                    {localData.note && (
                      <div className="flex items-center gap-1">
                        <FileText className="h-3 w-3 text-blue-600" />
                        <span className="text-xs text-gray-600">Note: {safeStringify(localData.note)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
      </CardContent>
    </Card>
  );
};

// Agreement Section
export const AgreementSection = ({ agreementData, setAgreementData }: AgreementSectionProps) => {
  const { hasPermission } = useAuthStore();
  
  // Check if user has permission to edit agreement information
  const canEditAgreement = hasPermission('EMPLOYEES_EDIT') || hasPermission('EMPLOYEES_CREATE');
  
  const [localIsEditing, setLocalIsEditing] = React.useState(false);
  const [localData, setLocalData] = React.useState<AgreementInfo[]>(() => {
    return Array.isArray(agreementData) ? agreementData : (agreementData ? [agreementData] : []);
  });
  const [_isAdding, setIsAdding] = React.useState(false);
  const [showAddModal, setShowAddModal] = React.useState(false);
  const [newAgreement, setNewAgreement] = React.useState({
    startDate: '',
    endDate: '',
    periodInMonths: '',
    notifyPeriodInDays: '',
    note: ''
  });

  // Helper function to calculate agreement end date
  const calculateAgreementEndDate = (startDate: string, periodInMonths: string): string => {
    if (!startDate || !periodInMonths) {
      return '';
    }

    try {
      const start = new Date(startDate);
      const months = parseInt(periodInMonths.trim(), 10);
      
      if (isNaN(months) || months <= 0) {
        return '';
      }

      const endDate = new Date(start);
      endDate.setMonth(endDate.getMonth() + months);
      
      // Subtract one day to get the last day of the period
      // For example: if start date is Jan 1 and period is 6 months, end date should be June 30
      endDate.setDate(endDate.getDate() - 1);
      
      return endDate.toISOString().split('T')[0]; // Return in YYYY-MM-DD format
    } catch (error) {
      console.error("Error calculating agreement end date:", error);
      return '';
    }
  };

  // Helper function to calculate days remaining until expiry
  const getDaysUntilExpiry = (agreement: AgreementInfo) => {
    if (!agreement.endDate) {
      return null;
    }
    
    const today = new Date();
    const endDate = new Date(agreement.endDate);
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  // Helper function to check if agreement needs notification
  const needsNotification = (agreement: AgreementInfo) => {
    if (!agreement.endDate || !agreement.notifyPeriodInDays) {
      return false;
    }
    
    const today = new Date();
    const endDate = new Date(agreement.endDate);
    const notifyDays = parseInt(agreement.notifyPeriodInDays, 10);
    
    if (isNaN(notifyDays)) {
      return false;
    }
    
    // Calculate notification date (end date - notify days)
    const notificationDate = new Date(endDate);
    notificationDate.setDate(notificationDate.getDate() - notifyDays);
    
    // Check if today is within the notification period
    return today >= notificationDate && today <= endDate;
  };

  // Helper function to determine agreement status
  const getAgreementStatus = (agreement: AgreementInfo) => {
    const today = new Date();
    const startDate = agreement.startDate ? new Date(agreement.startDate) : null;
    const endDate = agreement.endDate ? new Date(agreement.endDate) : null;
    
    if (!startDate) {
      return { status: 'draft', label: 'Draft', color: 'bg-gray-50 text-gray-600' };
    }
    
    if (today < startDate) {
      return { status: 'upcoming', label: 'Upcoming', color: 'bg-yellow-50 text-yellow-600' };
    }
    
    if (endDate && today > endDate) {
      return { status: 'expired', label: 'Expired', color: 'bg-red-50 text-red-600' };
    }
    
    // Check if agreement needs notification
    if (needsNotification(agreement)) {
      return { status: 'notification', label: 'Needs Attention', color: 'bg-orange-50 text-orange-600' };
    }
    
    return { status: 'active', label: 'Active', color: 'bg-blue-50 text-blue-600' };
  };

  React.useEffect(() => {
    // Ensure agreementData is always an array
    const dataArray = Array.isArray(agreementData) ? agreementData : (agreementData ? [agreementData] : []);
    
    // Process data to ensure proper format for form inputs
    const processedData = dataArray.map(agreement => {
      // Helper function to format date for HTML date input
      const formatDateForInput = (date: any) => {
        if (!date) return '';
        if (typeof date === 'string') {
          // If it's already a string, check if it's ISO format and convert
          if (date.includes('T')) {
            return new Date(date).toISOString().split('T')[0];
          }
          return date;
        }
        return new Date(date).toISOString().split('T')[0];
      };
      
      const processedAgreement = {
        startDate: formatDateForInput(agreement.startDate),
        endDate: formatDateForInput(agreement.endDate),
        periodInMonths: agreement.periodInMonths || '',
        notifyPeriodInDays: agreement.notifyPeriodInDays || '',
        note: agreement.note || ''
      };
      
      // Auto-calculate end date if start date and period are provided but end date is missing
      if (processedAgreement.startDate && processedAgreement.periodInMonths && !processedAgreement.endDate) {
        processedAgreement.endDate = calculateAgreementEndDate(processedAgreement.startDate, processedAgreement.periodInMonths);
      }
      
      return processedAgreement;
    });
    
    setLocalData(processedData);
  }, [agreementData]);

  const handleSave = () => {
    setAgreementData(localData);
    setLocalIsEditing(false);
    setIsAdding(false);
    toast.success('Agreement information updated successfully');
  };

  const handleCancel = () => {
    setLocalData(agreementData);
    setLocalIsEditing(false);
    setIsAdding(false);
    toast.info('Edit cancelled');
  };

  const handleEdit = () => {
    setLocalIsEditing(true);
    setIsAdding(false);
    toast.info('Edit mode enabled');
  };

  const handleAdd = () => {
    setShowAddModal(true);
  };

  const handleDelete = (index: number) => {
    const updatedData = localData.filter((_, i) => i !== index);
    setLocalData(updatedData);
    toast.success('Agreement deleted successfully');
  };

  const updateField = (index: number, field: keyof AgreementInfo, value: string) => {
    const updatedData = [...localData];
    updatedData[index] = {
      ...updatedData[index],
      [field]: value
    };

    // Auto-calculate end date if start date and period in months are provided
    if (field === 'startDate' && updatedData[index].periodInMonths) {
      updatedData[index].endDate = calculateAgreementEndDate(value, updatedData[index].periodInMonths);
    } else if (field === 'periodInMonths' && updatedData[index].startDate) {
      updatedData[index].endDate = calculateAgreementEndDate(updatedData[index].startDate, value);
    }

    setLocalData(updatedData);
  };

  // Handle new agreement form changes
  const handleNewAgreementChange = (field: keyof typeof newAgreement, value: string) => {
    const updatedAgreement = {
      ...newAgreement,
      [field]: value
    };

    // Auto-calculate end date if start date and period in months are provided
    if (field === 'startDate' && updatedAgreement.periodInMonths) {
      updatedAgreement.endDate = calculateAgreementEndDate(value, updatedAgreement.periodInMonths);
    } else if (field === 'periodInMonths' && updatedAgreement.startDate) {
      updatedAgreement.endDate = calculateAgreementEndDate(updatedAgreement.startDate, value);
    }

    setNewAgreement(updatedAgreement);
  };

  // Handle adding new agreement
  const handleAddAgreement = () => {
    if (!newAgreement.startDate || !newAgreement.periodInMonths) {
      toast.error('Please fill in required fields');
      return;
    }

    const agreementToAdd = {
      startDate: newAgreement.startDate,
      endDate: newAgreement.endDate || calculateAgreementEndDate(newAgreement.startDate, newAgreement.periodInMonths),
      periodInMonths: newAgreement.periodInMonths,
      notifyPeriodInDays: newAgreement.notifyPeriodInDays,
      note: newAgreement.note
    };

    const updatedData = [...localData, agreementToAdd];
    setLocalData(updatedData);
    setAgreementData(updatedData);
    
    // Reset form
    setNewAgreement({
      startDate: '',
      endDate: '',
      periodInMonths: '',
      notifyPeriodInDays: '',
      note: ''
    });
    
    setShowAddModal(false);
    toast.success('Agreement added successfully');
  };

  return (
    <>
    <Card className="border border-gray-200 shadow-sm bg-white rounded-lg">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <FileText className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-base font-semibold text-gray-900">
                Agreement
              </CardTitle>
             </div>
          </div>
          <div className="flex items-center gap-1">
            {canEditAgreement && localIsEditing && (
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                className="gap-1 bg-blue-600 hover:bg-blue-700 text-xs px-3 py-1"
              >
                <Save className="h-3 w-3" />
                Save
              </Button>
            )}
            {canEditAgreement && !localIsEditing && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleAdd}
                className="gap-1 text-green-600 hover:text-green-700 hover:bg-green-50 text-xs px-3 py-1"
              >
                <Plus className="h-3 w-3" />
                Add
              </Button>
            )}
            {canEditAgreement && (
            <Button
              variant="outline"
              size="sm"
              onClick={localIsEditing ? handleCancel : handleEdit}
              className="gap-1 hover:bg-gray-50 text-xs px-3 py-1"
            >
              {localIsEditing ? (
                <>
                  <X className="h-3 w-3" />
                  Cancel
                </>
              ) : (
                <>
                  <Edit2 className="h-3 w-3" />
                  Edit
                </>
              )}
            </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {localIsEditing ? (
          <>
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-blue-50 rounded-lg flex items-center justify-center">
                    <FileText className="h-3 w-3 text-blue-600" />
                  </div>
                  <h5 className="font-medium text-sm text-gray-700">
                    Agreement Details
                  </h5>
                </div>
              </div>

              <div className="space-y-4">
                {Array.isArray(localData) && localData.map((agreement, index) => (
                  <div key={index} className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-700">Agreement {index + 1}</h4>
                  {localData.length > 1 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(index)}
                      className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50 text-xs px-2 py-1"
                    >
                      <Trash2 className="h-3 w-3" />
                      Delete
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-700">Start Date</Label>
                    <Input
                      type="date"
                      value={agreement.startDate || ''}
                      onChange={(e) => updateField(index, 'startDate', e.target.value)}
                      className="h-8 text-xs"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-700">End Date</Label>
                    <Input
                      type="date"
                      value={agreement.endDate || ''}
                      onChange={(e) => updateField(index, 'endDate', e.target.value)}
                      className="h-8 text-xs"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-700">Period (In Months)</Label>
                    <Input
                      type="number"
                      value={agreement.periodInMonths}
                      onChange={(e) => updateField(index, 'periodInMonths', e.target.value)}
                      placeholder="Enter period in months"
                      className="h-8 text-xs"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-gray-700">Notify Period (In Days)</Label>
                    <Input
                      type="number"
                      value={agreement.notifyPeriodInDays}
                      onChange={(e) => updateField(index, 'notifyPeriodInDays', e.target.value)}
                      placeholder="Enter notify period in days"
                      className="h-8 text-xs"
                    />
                  </div>
                  
                  <div className="space-y-1 md:col-span-2">
                    <Label className="text-xs font-medium text-gray-700">Note</Label>
                    <Input
                      value={agreement.note}
                      onChange={(e) => updateField(index, 'note', e.target.value)}
                      placeholder="Enter additional notes"
                      className="h-8 text-xs"
                    />
                  </div>
                </div>
              </div>
            ))}
              </div>
            </div>
          </>
        ) : (
          <div className="space-y-3">
            {!Array.isArray(localData) || localData.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No agreements found</p>
                <p className="text-xs">Click "Add New" to create an agreement</p>
              </div>
            ) : (
              localData.map((agreement, index) => {
                const statusInfo = getAgreementStatus(agreement);
                return (
                <div key={index} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 bg-gradient-to-br rounded-full flex items-center justify-center ${
                        statusInfo.status === 'active' ? 'from-blue-50 to-blue-100' :
                        statusInfo.status === 'expired' ? 'from-red-50 to-red-100' :
                        statusInfo.status === 'upcoming' ? 'from-yellow-50 to-yellow-100' :
                        statusInfo.status === 'notification' ? 'from-orange-50 to-orange-100' :
                        'from-gray-50 to-gray-100'
                      }`}>
                        <FileText className={`h-4 w-4 ${
                          statusInfo.status === 'active' ? 'text-blue-600' :
                          statusInfo.status === 'expired' ? 'text-red-600' :
                          statusInfo.status === 'upcoming' ? 'text-yellow-600' :
                          statusInfo.status === 'notification' ? 'text-orange-600' :
                          'text-gray-600'
                        }`} />
                      </div>
                      <div>
                        <div className="font-medium text-sm text-gray-800">Employment Agreement {index + 1}</div>
                        <div className="text-xs text-gray-500">{statusInfo.label} Agreement</div>
                      </div>
                    </div>
                    <Badge variant="secondary" className={`${statusInfo.color} text-xs`}>
                      {statusInfo.label}
                    </Badge>
                  </div>
                  
                  {/* Notification message for agreements that need attention */}
                  {statusInfo.status === 'notification' && (() => {
                    const daysRemaining = getDaysUntilExpiry(agreement);
                    return (
                      <div className="mb-2 p-2 bg-orange-50 border border-orange-200 rounded-md">
                        <div className="flex items-center gap-1">
                          <Award className="h-3 w-3 text-orange-600" />
                          <span className="text-xs font-medium text-orange-800">
                            ⚠️ Agreement expires in {daysRemaining} days - New agreement needed!
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-gray-200">
                    {agreement.startDate && (
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3 text-blue-600" />
                        <span className="text-xs text-gray-600">Start: {formatDate(agreement.startDate)}</span>
                      </div>
                    )}
                    {agreement.endDate && (
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3 text-blue-600" />
                        <span className="text-xs text-gray-600">End: {formatDate(agreement.endDate)}</span>
                      </div>
                    )}
                    {agreement.periodInMonths && (
                      <div className="flex items-center gap-1">
                        <Award className="h-3 w-3 text-blue-600" />
                        <span className="text-xs text-gray-600">Period: {agreement.periodInMonths} months</span>
                      </div>
                    )}
                    {agreement.notifyPeriodInDays && (
                      <div className="flex items-center gap-1">
                        <Award className="h-3 w-3 text-blue-600" />
                        <span className="text-xs text-gray-600">Notify: {agreement.notifyPeriodInDays} days</span>
                      </div>
                    )}
                    {agreement.note && (
                      <div className="flex items-center gap-1 md:col-span-2">
                        <FileCheck className="h-3 w-3 text-blue-600" />
                        <span className="text-xs text-gray-600">Note: {agreement.note}</span>
                      </div>
                    )}
                  </div>
                </div>
                );
              })
            )}
          </div>
        )}
      </CardContent>
    </Card>

    {/* Add Agreement Modal */}
    <Dialog open={showAddModal} onOpenChange={(open) => {
      if (!open) {
        setShowAddModal(false);
      }
    }}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[95vh] overflow-hidden bg-white border-2 border-gray-200 shadow-2xl z-50">
        <DialogHeader className="pb-4">
          <DialogTitle className="text-lg font-semibold">
            Add New Agreement
          </DialogTitle>
        </DialogHeader>
        
        <div className="max-h-[75vh] overflow-y-auto px-4 pb-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-700">Start Date</Label>
              <Input
                type="date"
                className="h-9 text-sm"
                placeholder="Select start date"
                value={newAgreement.startDate}
                onChange={(e) => handleNewAgreementChange('startDate', e.target.value)}
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">End Date</Label>
              <Input
                type="date"
                className="h-9 text-sm"
                placeholder="Select end date"
                value={newAgreement.endDate}
                onChange={(e) => handleNewAgreementChange('endDate', e.target.value)}
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Period (Months)</Label>
              <Input
                type="number"
                className="h-9 text-sm"
                placeholder="Enter period in months"
                value={newAgreement.periodInMonths}
                onChange={(e) => handleNewAgreementChange('periodInMonths', e.target.value)}
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Notify Period (Days)</Label>
              <Input
                type="number"
                className="h-9 text-sm"
                placeholder="Enter notify period in days"
                value={newAgreement.notifyPeriodInDays}
                onChange={(e) => handleNewAgreementChange('notifyPeriodInDays', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700">Note</Label>
            <Input
              className="h-9 text-sm"
              placeholder="Enter additional notes"
              value={newAgreement.note}
              onChange={(e) => handleNewAgreementChange('note', e.target.value)}
            />
          </div>
        </div>
        
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => {
            setShowAddModal(false);
            // Reset form when canceling
            setNewAgreement({
              startDate: '',
              endDate: '',
              periodInMonths: '',
              notifyPeriodInDays: '',
              note: ''
            });
          }}>
            Cancel
          </Button>
          <Button onClick={handleAddAgreement}>
            Add Agreement
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

// Compensation Section
export const CompensationSection = ({ compensationData, setCompensationData }: CompensationSectionProps) => {
  const { hasPermission } = useAuthStore();
  
  // Check if user has permission to edit compensation information
  const canEditCompensation = hasPermission('EMPLOYEES_EDIT') || hasPermission('EMPLOYEES_CREATE');
  
  const [localIsEditing, setLocalIsEditing] = React.useState(false);
  const [localData, setLocalData] = React.useState<CompensationInfo[]>(() => {
    return Array.isArray(compensationData) ? compensationData : (compensationData ? [compensationData] : []);
  });
  const [_isAdding, setIsAdding] = React.useState(false);
  const [showAddCompensationModal, setShowAddCompensationModal] = React.useState(false);
  const [newCompensation, setNewCompensation] = React.useState({
    startDate: '',
    endDate: '',
    periodInMonths: '',
    previousSalary: '',
    incrementPercentage: '',
    newSalary: '',
    payFrequency: '',
    noticePeriodInDays: '',
    notifyPeriodInDays: '',
    monthlyBonus: '',
    salaryMode: '',
    note: ''
  });

  // Helper function to format date for HTML date input
  const formatDateForInput = (date: any) => {
    if (!date) return '';
    if (typeof date === 'string') {
      if (date.includes('T')) {
        return new Date(date).toISOString().split('T')[0];
      }
      return date;
    }
    return new Date(date).toISOString().split('T')[0];
  };

  React.useEffect(() => {
    // Ensure compensationData is always an array
    const dataArray = Array.isArray(compensationData) ? compensationData : (compensationData ? [compensationData] : []);
    
    const processedData = dataArray.map(compensation => {
      const processedCompensation = {
        startDate: formatDateForInput(compensation.startDate),
        endDate: formatDateForInput(compensation.endDate),
        periodInMonths: compensation.periodInMonths || '',
        previousSalary: compensation.previousSalary || '',
        incrementPercentage: compensation.incrementPercentage || '',
        newSalary: compensation.newSalary || '',
        payFrequency: compensation.payFrequency || '',
        noticePeriodInDays: compensation.noticePeriodInDays || '',
        notifyPeriodInDays: compensation.notifyPeriodInDays || '',
        monthlyBonus: compensation.monthlyBonus || '',
        salaryMode: compensation.salaryMode || '',
        note: compensation.note || ''
      };
      
      // Auto-calculate end date if start date and period in months are provided but end date is missing
      if (processedCompensation.startDate && processedCompensation.periodInMonths && !processedCompensation.endDate) {
        processedCompensation.endDate = calculateCompensationEndDate(processedCompensation.startDate, processedCompensation.periodInMonths);
      }
      
      // Auto-fill start date and previous salary from the last compensation entry
      const currentIndex = dataArray.indexOf(compensation);
      const lastCompensation = currentIndex > 0 ? dataArray[currentIndex - 1] : null;
      
      if (lastCompensation) {
        // Auto-fill start date with the end date of the previous compensation
        if (!processedCompensation.startDate && lastCompensation.endDate) {
          processedCompensation.startDate = formatDateForInput(lastCompensation.endDate);
        }
        
        // Auto-fill previous salary with the new salary of the previous compensation (only if completely empty)
        if (!processedCompensation.previousSalary && lastCompensation.newSalary) {
          processedCompensation.previousSalary = lastCompensation.newSalary;
        }
      }
      
      // Auto-calculate increment percentage if previous salary and new salary are available
      if (processedCompensation.previousSalary && processedCompensation.newSalary && !processedCompensation.incrementPercentage) {
        const previousSalary = parseFloat(processedCompensation.previousSalary);
        const newSalary = parseFloat(processedCompensation.newSalary);
        
        if (!isNaN(previousSalary) && !isNaN(newSalary) && previousSalary > 0) {
          const incrementPercentage = ((newSalary - previousSalary) / previousSalary) * 100;
          processedCompensation.incrementPercentage = incrementPercentage.toFixed(2);
        }
      }
      
      return processedCompensation;
    });
    
    setLocalData(processedData);
  }, [compensationData]);

  const handleSave = () => {
    setCompensationData(localData);
    setLocalIsEditing(false);
    setIsAdding(false);
    toast.success('Compensation information updated successfully');
  };

  const handleCancel = () => {
    setLocalData(compensationData);
    setLocalIsEditing(false);
    setIsAdding(false);
    toast.info('Edit cancelled');
  };

  const handleEdit = () => {
    setLocalIsEditing(true);
    setIsAdding(false);
    toast.info('Edit mode enabled');
  };

  const handleAdd = () => {
    setShowAddCompensationModal(true);
  };

  const handleDelete = (index: number) => {
    const updatedData = localData.filter((_, i) => i !== index);
    setLocalData(updatedData);
    toast.success('Compensation deleted successfully');
  };

  // Helper function to calculate compensation end date
  const calculateCompensationEndDate = (startDate: string, periodInMonths: string): string => {
    if (!startDate || !periodInMonths) {
      return '';
    }

    try {
      const start = new Date(startDate);
      const months = parseInt(periodInMonths.trim(), 10);
      
      if (isNaN(months) || months <= 0) {
        return '';
      }

      const endDate = new Date(start);
      endDate.setMonth(endDate.getMonth() + months);
      
      // Subtract one day to get the last day of the period
      // For example: if start date is Jan 1 and period is 6 months, end date should be June 30
      endDate.setDate(endDate.getDate() - 1);
      
      return endDate.toISOString().split('T')[0]; // Return in YYYY-MM-DD format
    } catch (error) {
      console.error("Error calculating compensation end date:", error);
      return '';
    }
  };

  // Helper function to calculate days remaining until expiry
  const getDaysUntilExpiry = (endDate: string) => {
    if (!endDate) {
      return null;
    }
    
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = end.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  // Helper function to check if compensation needs notification
  const needsNotification = (compensation: CompensationInfo) => {
    if (!compensation.endDate || !compensation.notifyPeriodInDays) {
      return false;
    }
    
    const today = new Date();
    const endDate = new Date(compensation.endDate);
    const notifyDays = parseInt(compensation.notifyPeriodInDays, 10);
    
    if (isNaN(notifyDays)) {
      return false;
    }
    
    // Calculate notification date (end date - notify days)
    const notificationDate = new Date(endDate);
    notificationDate.setDate(notificationDate.getDate() - notifyDays);
    
    // Check if today is within the notification period
    return today >= notificationDate && today <= endDate;
  };

  // Helper function to determine compensation status
  const getCompensationStatus = (compensation: CompensationInfo) => {
    const today = new Date();
    const startDate = compensation.startDate ? new Date(compensation.startDate) : null;
    const endDate = compensation.endDate ? new Date(compensation.endDate) : null;
    
    if (!startDate) {
      return { status: 'draft', label: 'Draft', color: 'bg-gray-50 text-gray-600' };
    }
    
    if (today < startDate) {
      return { status: 'upcoming', label: 'Upcoming', color: 'bg-yellow-50 text-yellow-600' };
    }
    
    if (endDate && today > endDate) {
      return { status: 'expired', label: 'Expired', color: 'bg-red-50 text-red-600' };
    }
    
    // Check if compensation needs notification
    if (needsNotification(compensation)) {
      return { status: 'notification', label: 'Expiring Soon', color: 'bg-orange-50 text-orange-600' };
    }
    
    return { status: 'active', label: 'Active', color: 'bg-blue-50 text-blue-600' };
  };

  const updateField = (index: number, field: keyof CompensationInfo, value: string) => {
    const updatedData = [...localData];
    updatedData[index] = {
      ...updatedData[index],
      [field]: value
    };

    // Auto-calculate end date if start date and period in months are provided
    if ((field === 'startDate' || field === 'periodInMonths') && updatedData[index].startDate && updatedData[index].periodInMonths) {
      updatedData[index].endDate = calculateCompensationEndDate(updatedData[index].startDate, updatedData[index].periodInMonths);
    }

    // Auto-calculate salary based on increment percentage
    if (field === 'incrementPercentage' && updatedData[index].previousSalary && value) {
      const previousSalary = parseFloat(updatedData[index].previousSalary);
      const incrementPercentage = parseFloat(value);
      
      if (!isNaN(previousSalary) && !isNaN(incrementPercentage)) {
        const newSalary = previousSalary + (previousSalary * incrementPercentage / 100);
        updatedData[index].newSalary = newSalary.toString();
      }
    }

    // Auto-calculate increment percentage based on new salary
    if (field === 'newSalary' && updatedData[index].previousSalary && value) {
      const previousSalary = parseFloat(updatedData[index].previousSalary);
      const newSalary = parseFloat(value);
      
      if (!isNaN(previousSalary) && !isNaN(newSalary) && previousSalary > 0) {
        const incrementPercentage = ((newSalary - previousSalary) / previousSalary) * 100;
        updatedData[index].incrementPercentage = incrementPercentage.toFixed(2);
      }
    }

    setLocalData(updatedData);
  };

  // Handle new compensation form changes
  const handleNewCompensationChange = (field: keyof typeof newCompensation, value: string) => {
    const updatedCompensation = {
      ...newCompensation,
      [field]: value
    };

    // Auto-calculate end date if start date and period in months are provided
    if ((field === 'startDate' || field === 'periodInMonths') && updatedCompensation.startDate && updatedCompensation.periodInMonths) {
      updatedCompensation.endDate = calculateCompensationEndDate(updatedCompensation.startDate, updatedCompensation.periodInMonths);
    }

    // Auto-calculate salary based on increment percentage
    if (field === 'incrementPercentage' && updatedCompensation.previousSalary && value) {
      const previousSalary = parseFloat(updatedCompensation.previousSalary);
      const incrementPercentage = parseFloat(value);
      
      if (!isNaN(previousSalary) && !isNaN(incrementPercentage)) {
        const newSalary = previousSalary + (previousSalary * incrementPercentage / 100);
        updatedCompensation.newSalary = newSalary.toString();
      }
    }

    // Auto-calculate increment percentage based on new salary
    if (field === 'newSalary' && updatedCompensation.previousSalary && value) {
      const previousSalary = parseFloat(updatedCompensation.previousSalary);
      const newSalary = parseFloat(value);
      
      if (!isNaN(previousSalary) && !isNaN(newSalary) && previousSalary > 0) {
        const incrementPercentage = ((newSalary - previousSalary) / previousSalary) * 100;
        updatedCompensation.incrementPercentage = incrementPercentage.toFixed(2);
      }
    }

    setNewCompensation(updatedCompensation);
  };

  // Handle adding new compensation
  const handleAddCompensation = () => {
    if (!newCompensation.startDate || !newCompensation.periodInMonths) {
      toast.error('Please fill in required fields');
      return;
    }

    const compensationToAdd = {
      startDate: newCompensation.startDate,
      endDate: newCompensation.endDate || calculateCompensationEndDate(newCompensation.startDate, newCompensation.periodInMonths),
      periodInMonths: newCompensation.periodInMonths,
      previousSalary: newCompensation.previousSalary,
      incrementPercentage: newCompensation.incrementPercentage,
      newSalary: newCompensation.newSalary,
      payFrequency: newCompensation.payFrequency,
      noticePeriodInDays: newCompensation.noticePeriodInDays,
      notifyPeriodInDays: newCompensation.notifyPeriodInDays,
      monthlyBonus: newCompensation.monthlyBonus,
      salaryMode: newCompensation.salaryMode,
      note: newCompensation.note
    };

    const updatedData = [...localData, compensationToAdd];
    setLocalData(updatedData);
    setCompensationData(updatedData);
    
    // Reset form
    setNewCompensation({
      startDate: '',
      endDate: '',
      periodInMonths: '',
      previousSalary: '',
      incrementPercentage: '',
      newSalary: '',
      payFrequency: '',
      noticePeriodInDays: '',
      notifyPeriodInDays: '',
      monthlyBonus: '',
      salaryMode: '',
      note: ''
    });
    
    setShowAddCompensationModal(false);
    toast.success('Compensation added successfully');
  };

  return (
    <>
    <Card className="border-none shadow-sm bg-white">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base font-bold bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
              Compensation
            </CardTitle>
          </div>
          <div className="flex items-center gap-1">
            {canEditCompensation && localIsEditing && (
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                className="gap-1 bg-blue-600 hover:bg-blue-700 text-xs px-2 py-1"
              >
                <Save className="h-3 w-3" />
                Save
              </Button>
            )}
            {canEditCompensation && !localIsEditing && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleAdd}
                className="gap-1 text-green-600 hover:text-green-700 hover:bg-green-50 text-xs px-2 py-1"
              >
                <Plus className="h-3 w-3" />
                Add
              </Button>
            )}
            {canEditCompensation && (
            <Button
              variant="outline"
              size="sm"
              onClick={localIsEditing ? handleCancel : handleEdit}
              className="gap-1 hover:bg-gray-100 transition-colors text-xs px-2 py-1"
            >
              {localIsEditing ? (
                <>
                  <X className="h-3 w-3" />
                  Cancel
                </>
              ) : (
                <>
                  <Edit2 className="h-3 w-3" />
                  Edit
                </>
              )}
            </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {localIsEditing ? (
          <div className="space-y-4">
            {Array.isArray(localData) && localData.map((compensation, index) => (
              <div key={index} className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-700">Compensation {index + 1}</h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(index)}
                    className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50 text-xs px-2 py-1"
                  >
                    <Trash2 className="h-3 w-3" />
                    Delete
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">Start Date</Label>
                  <Input
                    type="date"
                    value={compensation.startDate}
                    onChange={(e) => updateField(index, 'startDate', e.target.value)}
                    className="h-8 text-xs"
                  />
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">End Date</Label>
                  <Input
                    type="date"
                    value={compensation.endDate}
                    onChange={(e) => updateField(index, 'endDate', e.target.value)}
                    className="h-8 text-xs"
                  />
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">Period (in Months)</Label>
                  <Input
                    type="number"
                    value={compensation.periodInMonths}
                    onChange={(e) => updateField(index, 'periodInMonths', e.target.value)}
                    placeholder="Enter period in months"
                    className="h-8 text-xs"
                  />
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">Previous Salary</Label>
                  <Input
                    type="number"
                    value={compensation.previousSalary || ''}
                    onChange={(e) => updateField(index, 'previousSalary', e.target.value)}
                    placeholder="Enter previous salary"
                    className="h-8 text-xs"
                    disabled={false}
                  />
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">Increment %</Label>
                  <Input
                    type="number"
                    value={compensation.incrementPercentage}
                    onChange={(e) => updateField(index, 'incrementPercentage', e.target.value)}
                    placeholder="Enter increment percentage"
                    className="h-8 text-xs"
                  />
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">New Salary</Label>
                  <Input
                    type="number"
                    value={compensation.newSalary}
                    onChange={(e) => updateField(index, 'newSalary', e.target.value)}
                    placeholder="Enter new salary"
                    className="h-8 text-xs"
                  />
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">Pay Frequency</Label>
                  <Select 
                    value={compensation.payFrequency || ''} 
                    onValueChange={(value) => updateField(index, 'payFrequency', value === 'clear' ? '' : value)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select pay frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="clear">
                        <span className="text-gray-500 italic">Clear selection</span>
                      </SelectItem>
                      <SelectItem value="Monthly">Monthly</SelectItem>
                      <SelectItem value="Bi-weekly">Bi-weekly</SelectItem>
                      <SelectItem value="Weekly">Weekly</SelectItem>
                      <SelectItem value="Quarterly">Quarterly</SelectItem>
                      <SelectItem value="Annually">Annually</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">Notice Period (In Days)</Label>
                  <Input
                    type="number"
                    value={compensation.noticePeriodInDays}
                    onChange={(e) => updateField(index, 'noticePeriodInDays', e.target.value)}
                    placeholder="Enter notice period in days"
                    className="h-8 text-xs"
                  />
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">Notify Period (In Days)</Label>
                  <Input
                    type="number"
                    value={compensation.notifyPeriodInDays}
                    onChange={(e) => updateField(index, 'notifyPeriodInDays', e.target.value)}
                    placeholder="Enter notify period in days"
                    className="h-8 text-xs"
                  />
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">Monthly Bonus</Label>
                  <Input
                    type="number"
                    value={compensation.monthlyBonus}
                    onChange={(e) => updateField(index, 'monthlyBonus', e.target.value)}
                    placeholder="Enter monthly bonus"
                    className="h-8 text-xs"
                  />
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-700">Salary Mode</Label>
                  <Select 
                    value={compensation.salaryMode || ''} 
                    onValueChange={(value) => updateField(index, 'salaryMode', value === 'clear' ? '' : value)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select salary mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="clear">
                        <span className="text-gray-500 italic">Clear selection</span>
                      </SelectItem>
                      <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                      <SelectItem value="Cash">Cash</SelectItem>
                      <SelectItem value="Check">Check</SelectItem>
                      <SelectItem value="Digital Payment">Digital Payment</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-1 md:col-span-2">
                  <Label className="text-xs font-medium text-gray-700">Note</Label>
                  <Input
                    value={compensation.note}
                    onChange={(e) => updateField(index, 'note', e.target.value)}
                    placeholder="Enter additional notes"
                    className="h-8 text-xs"
                  />
                </div>
              </div>
            </div>
            ))}
           
          </div>
        ) : (
          <div className="space-y-3">
            {!Array.isArray(localData) || localData.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CreditCard className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No compensation records found</p>
                <p className="text-xs">Click "Add New" to create a compensation record</p>
              </div>
            ) : (
              localData.map((compensation, index) => {
                const statusInfo = getCompensationStatus(compensation);
                return (
                  <div key={index} className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-50 to-blue-100 rounded-full flex items-center justify-center">
                          <CreditCard className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-medium text-xs text-gray-800">Compensation Details {index + 1}</div>
                          <div className="text-xs text-gray-500 mb-2">{statusInfo.label} Compensation</div>
                        </div>
                      </div>
                      <Badge variant="secondary" className={`${statusInfo.color} text-xs`}>
                        {statusInfo.label}
                      </Badge>
                    </div>
                    
                    {/* Notification message for compensation that needs attention */}
                    {statusInfo.status === 'notification' && (() => {
                      const daysRemaining = getDaysUntilExpiry(compensation.endDate);
                      return (
                        <div className="mb-2 p-2 bg-orange-50 border border-orange-200 rounded-md">
                          <div className="flex items-center gap-1">
                            <CreditCard className="h-3 w-3 text-orange-600" />
                            <span className="text-xs font-medium text-orange-800">
                              ⚠️ Compensation expires in {daysRemaining} days - New compensation needed!
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-2 border-t border-gray-100">
                {compensation.startDate && (
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3 text-blue-600" />
                    <span className="text-xs text-gray-600">Start: {formatDate(compensation.startDate)}</span>
                  </div>
                )}
                {compensation.endDate && (
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3 text-blue-600" />
                    <span className="text-xs text-gray-600">End: {formatDate(compensation.endDate)}</span>
                  </div>
                )}
                {compensation.periodInMonths && (
                  <div className="flex items-center gap-1">
                    <Award className="h-3 w-3 text-blue-600" />
                    <span className="text-xs text-gray-600">Period: {compensation.periodInMonths} months</span>
                  </div>
                )}
                {compensation.previousSalary && (
                  <div className="flex items-center gap-1">
                    <CreditCard className="h-3 w-3 text-blue-600" />
                    <span className="text-xs text-gray-600">Previous: ₹{compensation.previousSalary}</span>
                  </div>
                )}
                {compensation.incrementPercentage && (
                  <div className="flex items-center gap-1">
                    <Award className="h-3 w-3 text-blue-600" />
                    <span className="text-xs text-gray-600">Increment: {compensation.incrementPercentage}%</span>
                  </div>
                )}
                {compensation.newSalary && (
                  <div className="flex items-center gap-1">
                    <CreditCard className="h-3 w-3 text-blue-600" />
                    <span className="text-xs text-gray-600">New: ₹{compensation.newSalary}</span>
                  </div>
                )}
                {compensation.payFrequency && (
                  <div className="flex items-center gap-1">
                    <Award className="h-3 w-3 text-blue-600" />
                    <span className="text-xs text-gray-600">Frequency: {compensation.payFrequency}</span>
                  </div>
                )}
                {compensation.noticePeriodInDays && (
                  <div className="flex items-center gap-1">
                    <Award className="h-3 w-3 text-blue-600" />
                    <span className="text-xs text-gray-600">Notice: {compensation.noticePeriodInDays} days</span>
                  </div>
                )}
                {compensation.notifyPeriodInDays && (
                  <div className="flex items-center gap-1">
                    <Award className="h-3 w-3 text-blue-600" />
                    <span className="text-xs text-gray-600">Notify: {compensation.notifyPeriodInDays} days</span>
                  </div>
                )}
                {compensation.monthlyBonus && (
                  <div className="flex items-center gap-1">
                    <Award className="h-3 w-3 text-blue-600" />
                    <span className="text-xs text-gray-600">Bonus: ₹{compensation.monthlyBonus}</span>
                  </div>
                )}
                {compensation.note && (
                  <div className="flex items-center gap-1 md:col-span-2">
                    <FileCheck className="h-3 w-3 text-blue-600" />
                    <span className="text-xs text-gray-600">Note: {compensation.note}</span>
                  </div>
                )}
                  </div>
                </div>
                );
              })
            )}
          </div>
        )}
      </CardContent>
    </Card>

    {/* Add Compensation Modal */}
    <Dialog open={showAddCompensationModal} onOpenChange={(open) => {
      if (!open) {
        setShowAddCompensationModal(false);
      }
    }}>
      <DialogContent className="w-[95vw] max-w-4xl max-h-[95vh] overflow-hidden bg-white border-2 border-gray-200 shadow-2xl z-50">
        <DialogHeader className="pb-4">
          <DialogTitle className="text-lg font-semibold">
            Add New Compensation
          </DialogTitle>
        </DialogHeader>
        
        <div className="max-h-[75vh] overflow-y-auto px-4 pb-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pb-4">
            <div>
              <Label className="text-sm font-medium text-gray-700">Start Date</Label>
              <Input
                type="date"
                className="h-9 text-sm"
                placeholder="Select start date"
                value={newCompensation.startDate}
                onChange={(e) => handleNewCompensationChange('startDate', e.target.value)}
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">End Date</Label>
              <Input
                type="date"
                className="h-9 text-sm"
                placeholder="Select end date"
                value={newCompensation.endDate}
                onChange={(e) => handleNewCompensationChange('endDate', e.target.value)}
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Period (Months)</Label>
              <Input
                type="number"
                className="h-9 text-sm"
                placeholder="Enter period in months"
                value={newCompensation.periodInMonths}
                onChange={(e) => handleNewCompensationChange('periodInMonths', e.target.value)}
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Previous Salary</Label>
              <Input
                type="number"
                className="h-9 text-sm"
                placeholder="Enter previous salary"
                value={newCompensation.previousSalary}
                onChange={(e) => handleNewCompensationChange('previousSalary', e.target.value)}
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Increment %</Label>
              <Input
                type="number"
                className="h-9 text-sm"
                placeholder="Enter increment percentage"
                value={newCompensation.incrementPercentage}
                onChange={(e) => handleNewCompensationChange('incrementPercentage', e.target.value)}
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">New Salary</Label>
              <Input
                type="number"
                className="h-9 text-sm"
                placeholder="Enter new salary"
                value={newCompensation.newSalary}
                onChange={(e) => handleNewCompensationChange('newSalary', e.target.value)}
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Pay Frequency</Label>
              <Select
                value={newCompensation.payFrequency}
                onValueChange={(value) => handleNewCompensationChange('payFrequency', value)}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Select frequency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Notice Period (Days)</Label>
              <Input
                type="number"
                className="h-9 text-sm"
                placeholder="Enter notice period"
                value={newCompensation.noticePeriodInDays}
                onChange={(e) => handleNewCompensationChange('noticePeriodInDays', e.target.value)}
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Notify Period (Days)</Label>
              <Input
                type="number"
                className="h-9 text-sm"
                placeholder="Enter notify period"
                value={newCompensation.notifyPeriodInDays}
                onChange={(e) => handleNewCompensationChange('notifyPeriodInDays', e.target.value)}
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Monthly Bonus</Label>
              <Input
                type="number"
                className="h-9 text-sm"
                placeholder="Enter monthly bonus"
                value={newCompensation.monthlyBonus}
                onChange={(e) => handleNewCompensationChange('monthlyBonus', e.target.value)}
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Salary Mode</Label>
              <Select
                value={newCompensation.salaryMode}
                onValueChange={(value) => handleNewCompensationChange('salaryMode', value)}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Select mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="digital_transfer">Digital Transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4">
            <Label className="text-sm font-medium text-gray-700">Note</Label>
            <Input
              className="h-9 text-sm"
              placeholder="Enter additional notes"
              value={newCompensation.note}
              onChange={(e) => handleNewCompensationChange('note', e.target.value)}
            />
          </div>
        </div>
        
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => {
            setShowAddCompensationModal(false);
            // Reset form when canceling
            setNewCompensation({
              startDate: '',
              endDate: '',
              periodInMonths: '',
              previousSalary: '',
              incrementPercentage: '',
              newSalary: '',
              payFrequency: '',
              noticePeriodInDays: '',
              notifyPeriodInDays: '',
              monthlyBonus: '',
              salaryMode: '',
              note: ''
            });
          }}>
            Cancel
          </Button>
          <Button onClick={handleAddCompensation}>
            Add Compensation
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

// Employee Resignation Section
export const ResignationSection = ({ resignationData, setResignationData, employeeData }: ResignationSectionProps) => {
  const { hasPermission, user } = useAuthStore();
  
  // Business Logic for Resignation Process:
  // 1. Employees cannot submit their own resignation
  // 2. HR can add resignation for employees
  // 3. HR's resignation should be added by their higher authority
  
  // Check if current user is HR
  const isCurrentUserHR = user?.role === 'HR Manager';
  
  // Check if viewing employee is HR (the employee whose profile is open)
  const isViewingEmployeeHR = employeeData?.user?.role === 'HR Manager' || 
                             employeeData?.user?.role === 'HR' ||
                             employeeData?.user?.role === 'SUPER_ADMIN' ||
                             employeeData?.user?.role === 'Super Admin' ||
                             (Array.isArray(employeeData?.user?.roles) && employeeData.user.roles.some((role: any) => 
                               (typeof role === 'string' && (role.includes('HR') || role.includes('SUPER_ADMIN'))) ||
                               (typeof role === 'object' && role.name && (role.name.includes('HR') || role.name.includes('SUPER_ADMIN')))
                             ));
  
  // Check if user is higher authority (Super Admin or above HR level)
  const isHigherAuthority = user?.role === 'Super Admin' || user?.role === 'SUPER_ADMIN' || user?.role === 'Admin';
  
  // Check if user is trying to resign themselves
  const isSelfResignation = !employeeData || (user?.id === employeeData?.user?._id);

  // 4. Must have basic employee permissions
  const canEditResignation = (hasPermission('EMPLOYEES_EDIT') || hasPermission('EMPLOYEES_CREATE')) && 
                            !isSelfResignation && 
                            (
                              (isCurrentUserHR && !isViewingEmployeeHR) ||  // HR can resign non-HR employees
                              (isHigherAuthority) ||                        // Higher authority can resign anyone
                              (!isCurrentUserHR && !isViewingEmployeeHR)    // Non-HR users can resign non-HR employees
                            );
  
  const [localIsEditing, setLocalIsEditing] = React.useState(false);
  const [localData, setLocalData] = React.useState(resignationData || {
    resignationType: '',
    reason: '',
    noticeGivenOn: '',
    lastWorkingDay: '',
    note: '',
    revokeReason: '',
    remark: '',
    revokedOn: '',
    status: ''
  });
  const [_isAdding, setIsAdding] = React.useState(false);
  const [showAddModal, setShowAddModal] = React.useState(false);
  const [addFormData, setAddFormData] = React.useState({
    selectedEmployeeId: '',
    resignationType: 'Employee wants to resign',
    reason: '',
    noticeGivenOn: '',
    lastWorkingDay: '',
    note: '',
    revokeReason: '',
    remark: '',
    revokedOn: '',
    status: 'Pending'
  });

  
  // State for employees list
  const [_employeesList, setEmployeesList] = React.useState<any[]>([]);

  // Fetch employees list for dropdown
  React.useEffect(() => {
    const fetchEmployees = async () => {
    try {
        const employees = await getEmployeesForDropdown();
        setEmployeesList(employees);
    } catch (error) {
        console.error('Error fetching employees:', error);
    }
    };
    fetchEmployees();
  }, []);

  // Fetch profile when modal opens - commented out to prevent auto-filling
  // React.useEffect(() => {
  //   if (showAddModal && !employeeProfile) {
  //     fetchEmployeeProfile();
  //   }
  // }, [showAddModal, employeeProfile, fetchEmployeeProfile]);


  React.useEffect(() => {
    setLocalData(resignationData || {
      resignationType: '',
      reason: '',
      noticeGivenOn: '',
      lastWorkingDay: '',
      note: '',
      revokeReason: '',
      remark: '',
      revokedOn: '',
      status: ''
    });
  }, [resignationData]);


  const handleAdd = () => {
    setShowAddModal(true);
    setAddFormData({
      selectedEmployeeId: employeeData?.user?._id || '',
      resignationType: 'Employee wants to resign',
      reason: '',
      noticeGivenOn: '',
      lastWorkingDay: '',
      note: '',
      revokeReason: '',
      remark: '',
      revokedOn: '',
      status: 'Pending'
    });
    setLocalIsEditing(false);
    setIsAdding(true);
  };

  const handleEdit = () => {
    setShowAddModal(true);
    // Pre-fill the form with existing data for editing
    setAddFormData({
      selectedEmployeeId: employeeData?.user?._id || '',
      resignationType: localData.resignationType || 'Employee wants to resign',
      reason: localData.reason || '',
      noticeGivenOn: localData.noticeGivenOn || '',
      lastWorkingDay: localData.lastWorkingDay || '',
      note: localData.note || '',
      revokeReason: localData.revokeReason || '',
      remark: localData.remark || '',
      revokedOn: localData.revokedOn || '',
      status: localData.status || 'Pending'
    });
    setLocalIsEditing(true);
    setIsAdding(false);
    toast.info('Edit mode enabled');
  };

  const handleAddSave = () => {
    // Create new resignation data from modal form
    const newResignationData = {
      resignationType: addFormData.resignationType,
      reason: addFormData.reason,
      noticeGivenOn: addFormData.noticeGivenOn,
      lastWorkingDay: addFormData.lastWorkingDay,
      note: addFormData.note,
      revokeReason: addFormData.revokeReason,
      remark: addFormData.remark,
      revokedOn: addFormData.revokedOn,
      status: addFormData.status
    };
    
    setResignationData(newResignationData);
    setLocalData(newResignationData);
    setShowAddModal(false);
    setLocalIsEditing(false);
    setIsAdding(false);
    
    // Reset form data after successful save
    setAddFormData({
      selectedEmployeeId: employeeData?.user?._id || '',
      resignationType: 'Employee wants to resign',
      reason: '',
      noticeGivenOn: '',
      lastWorkingDay: '',
      note: '',
      revokeReason: '',
      remark: '',
      revokedOn: '',
      status: 'Pending'
    });
    
    toast.success(localIsEditing ? 'Resignation information updated successfully' : 'Resignation information added successfully');
  };

  const handleAddCancel = () => {
    setShowAddModal(false);
    setLocalIsEditing(false);
    setIsAdding(false);
    // Reset form data to original state
    setAddFormData({
      selectedEmployeeId: employeeData?.user?._id || '',
      resignationType: 'Employee wants to resign',
      reason: '',
      noticeGivenOn: '',
      lastWorkingDay: '',
      note: '',
      revokeReason: '',
      remark: '',
      revokedOn: '',
      status: 'Pending'
    });
    toast.info('Operation cancelled');
  };


  const updateAddFormField = (field: string, value: string) => {
    setAddFormData({
      ...addFormData,
      [field]: value
    });
  };

  const isFormMode = localIsEditing || _isAdding;

  // Check if there's any meaningful data to display
  const hasData = localData.resignationType || localData.reason || localData.note || 
                  localData.noticeGivenOn || localData.lastWorkingDay || localData.status ||
                  localData.revokeReason || localData.remark || localData.revokedOn;



  // Helper function to get restriction title
  const getResignationRestrictionTitle = () => {
    // Only show restriction title if user actually cannot edit
    if (!canEditResignation) {
      if (isSelfResignation) {
        if (isCurrentUserHR) {
          return "HR Resignation Access Restricted";
        } else {
          return "Self-Resignation Not Allowed";
        }
      }
      if (isViewingEmployeeHR && !isHigherAuthority) {
        return "HR Resignation Access Restricted";
      }
      if (!isCurrentUserHR && !isHigherAuthority) {
        return "Resignation Access Restricted";
      }
    }
    return "Resignation Access Restricted";
  };

  return (
    <>
      <Card className="border border-gray-200 shadow-sm bg-white rounded-lg">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <Users className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-gray-900">
                  Employee Resignation
                </CardTitle>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {canEditResignation && !isFormMode && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleAdd}
                  className="gap-1 bg-green-600 hover:bg-green-700 text-xs px-3 py-1"
                >
                  <Users className="h-3 w-3" />
                  Add
                </Button>
              )}
              {canEditResignation && hasData && !isFormMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEdit}
                  className="gap-1 hover:bg-gray-50 text-xs px-3 py-1"
                >
                  <Edit2 className="h-3 w-3" />
                  Edit
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">
          {/* Display restriction message if user cannot add resignation */}
          {!canEditResignation && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-amber-600 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">{getResignationRestrictionTitle()}</p>
                  
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {hasData ? (
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-blue-50 rounded-lg flex items-center justify-center">
                        <Users className="h-3 w-3 text-blue-600" />
                      </div>
                      <div>
                        <div className="font-medium text-sm text-gray-900">
                          {localData.resignationType || 'Resignation Type'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {localData.status || 'Pending'} Status
                        </div>
                      </div>
                    </div>
                    <Badge 
                      variant="secondary" 
                      className={`text-xs ${
                        localData.status === 'Approved' ? 'bg-green-50 text-green-600' :
                        localData.status === 'Rejected' ? 'bg-red-50 text-red-600' :
                        localData.status === 'Revoked' ? 'bg-orange-50 text-orange-600' :
                        localData.status === 'Completed' ? 'bg-blue-50 text-blue-600' :
                        'bg-yellow-50 text-yellow-600'
                      }`}
                    >
                      {localData.status || 'Pending'}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1 border-t border-gray-200">
                    {localData.noticeGivenOn && (
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3 text-blue-600" />
                        <span className="text-xs text-gray-600">Notice: {formatDate(localData.noticeGivenOn)}</span>
                      </div>
                    )}
                    {localData.lastWorkingDay && (
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3 text-blue-600" />
                        <span className="text-xs text-gray-600">Last Day: {formatDate(localData.lastWorkingDay)}</span>
                      </div>
                    )}
                    {localData.revokedOn && (
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3 text-blue-600" />
                        <span className="text-xs text-gray-600">Revoked: {formatDate(localData.revokedOn)}</span>
                      </div>
                    )}
                    {localData.reason && (
                      <div className="flex items-center gap-1">
                        <FileCheck className="h-3 w-3 text-blue-600" />
                        <span className="text-xs text-gray-600">Reason: {localData.reason}</span>
                      </div>
                    )}
                    {localData.revokeReason && (
                      <div className="flex items-center gap-1">
                        <FileCheck className="h-3 w-3 text-blue-600" />
                        <span className="text-xs text-gray-600">Revoke: {localData.revokeReason}</span>
                      </div>
                    )}
                    {localData.remark && (
                      <div className="flex items-center gap-1">
                        <FileCheck className="h-3 w-3 text-blue-600" />
                        <span className="text-xs text-gray-600">Remark: {localData.remark}</span>
                      </div>
                    )}
                    {localData.note && (
                      <div className="flex items-center gap-1">
                        <FileCheck className="h-3 w-3 text-blue-600" />
                        <span className="text-xs text-gray-600">Note: {localData.note}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">No Resignation Records</h3>
                <p className="text-sm text-gray-500 mb-4">No resignation information has been recorded for this employee.</p>
                {canEditResignation && (
                  <div className="flex flex-col items-center gap-2">
                    <p className="text-xs text-gray-400">Click "Add" to create a new resignation record</p>
                    <div className="w-32 h-1 bg-gray-200 rounded-full"></div>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Employee Resignation Modal */}
      <Dialog open={showAddModal} onOpenChange={(open) => {
        // Only allow closing through Cancel/Update buttons, not by clicking outside
        if (!open) {
          return; // Prevent closing on outside click
        }
        setShowAddModal(open);
      }}>
        <DialogContent 
          className="max-w-4xl max-h-[95vh] overflow-hidden bg-white border-2 border-gray-200 shadow-2xl z-50"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader className="pb-4">
            <DialogTitle className="text-lg font-semibold">
              {localIsEditing ? 'Edit Employee Resignation' : 'Add Employee Resignation'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="max-h-[75vh] overflow-y-auto pr-2 pb-4 space-y-6">
            {/* Employee Details Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">Employee Details</h3>
              {employeeData ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-medium text-blue-600">
                        {employeeData.user?.firstName?.charAt(0) || ''}{employeeData.user?.lastName?.charAt(0) || ''}
                      </span>
                    </div>
                    <div>
                      <div className="text-sm font-medium">
                        {employeeData.user?.firstName} {employeeData.user?.lastName}
                  </div>
                      <div className="text-xs text-gray-500">
                        {employeeData.user?.employeeId}
                </div>
                    </div>
                    </div>
                  <div className="text-xs text-gray-600">Department: {employeeData.department?.name || 'N/A'}</div>
                  <div className="text-xs text-gray-600">Location: {employeeData.jobInfo?.location || 'N/A'}</div>
                  </div>
              ) : (
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-gray-700">Select Employee</Label>
                  <Select
                    value={addFormData.selectedEmployeeId || ''}
                    onValueChange={(value) => setAddFormData(prev => ({ ...prev, selectedEmployeeId: value }))}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {_employeesList.map((employee) => (
                        <SelectItem key={employee._id || employee.id} value={employee._id || employee.id || ''}>
                          {employee.name} ({employee.employeeId})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Resignation Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Reason for Resignation Section */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-700">Reason for initiating Resignation</h3>
                
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="employee-resign"
                      name="resignationType"
                      value="Employee wants to resign"
                      checked={addFormData.resignationType === "Employee wants to resign"}
                      onChange={(e) => updateAddFormField('resignationType', e.target.value)}
                      className="h-4 w-4 rounded-full border border-primary text-primary ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    />
                    <Label htmlFor="employee-resign" className="text-sm">Employee wants to resign</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="company-relieve"
                      name="resignationType"
                      value="Company Decides to relieve"
                      checked={addFormData.resignationType === "Company Decides to relieve"}
                      onChange={(e) => updateAddFormField('resignationType', e.target.value)}
                      className="h-4 w-4 rounded-full border border-primary text-primary ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    />
                    <Label htmlFor="company-relieve" className="text-sm">Company Decides to relieve</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="absconding"
                      name="resignationType"
                      value="Absconding"
                      checked={addFormData.resignationType === "Absconding"}
                      onChange={(e) => updateAddFormField('resignationType', e.target.value)}
                      className="h-4 w-4 rounded-full border border-primary text-primary ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    />
                    <Label htmlFor="absconding" className="text-sm">Absconding</Label>
                    <Info className="h-3 w-3 text-gray-400" />
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="passed-away"
                      name="resignationType"
                      value="Employee Passed Away"
                      checked={addFormData.resignationType === "Employee Passed Away"}
                      onChange={(e) => updateAddFormField('resignationType', e.target.value)}
                      className="h-4 w-4 rounded-full border border-primary text-primary ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    />
                    <Label htmlFor="passed-away" className="text-sm">Employee Passed Away</Label>
                    <Info className="h-3 w-3 text-gray-400" />
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="retirement"
                      name="resignationType"
                      value="Retirement"
                      checked={addFormData.resignationType === "Retirement"}
                      onChange={(e) => updateAddFormField('resignationType', e.target.value)}
                      className="h-4 w-4 rounded-full border border-primary text-primary ring-offset-background focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    />
                    <Label htmlFor="retirement" className="text-sm">Retirement</Label>
                    <Info className="h-3 w-3 text-gray-400" />
                  </div>
                </div>
              </div>

              {/* Resignation Details */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-700">Resignation Details</h3>
                
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Reason for Resignation *</Label>
                  <Select value={addFormData.reason} onValueChange={(value) => updateAddFormField('reason', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select reason" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Better opportunity">Better opportunity</SelectItem>
                      <SelectItem value="Personal reasons">Personal reasons</SelectItem>
                      <SelectItem value="Health issues">Health issues</SelectItem>
                      <SelectItem value="Relocation">Relocation</SelectItem>
                      <SelectItem value="Career change">Career change</SelectItem>
                      <SelectItem value="Performance issues">Performance issues</SelectItem>
                      <SelectItem value="Company restructuring">Company restructuring</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Notice Given On *</Label>
                  <Input
                    type="date"
                    value={addFormData.noticeGivenOn}
                    onChange={(e) => updateAddFormField('noticeGivenOn', e.target.value)}
                    className="h-8 text-xs"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Last Working Day *</Label>
                  <Input
                    type="date"
                    value={addFormData.lastWorkingDay}
                    onChange={(e) => updateAddFormField('lastWorkingDay', e.target.value)}
                    className="h-8 text-xs"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Status</Label>
                  <Select value={addFormData.status} onValueChange={(value) => updateAddFormField('status', value)}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Approved">Approved</SelectItem>
                      <SelectItem value="Rejected">Rejected</SelectItem>
                      <SelectItem value="Revoked">Revoked</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">Additional Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Note *</Label>
                  <Textarea
                    value={addFormData.note}
                    onChange={(e) => updateAddFormField('note', e.target.value)}
                    placeholder="Enter additional notes..."
                    className="min-h-[80px]"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Remark</Label>
                  <Textarea
                    value={addFormData.remark}
                    onChange={(e) => updateAddFormField('remark', e.target.value)}
                    placeholder="Enter remarks (optional)"
                    className="min-h-[80px]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Revoke Reason</Label>
                  <Input
                    value={addFormData.revokeReason}
                    onChange={(e) => updateAddFormField('revokeReason', e.target.value)}
                    placeholder="Enter revoke reason (optional)"
                    className="h-8 text-xs"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Revoked On</Label>
                  <Input
                    type="date"
                    value={addFormData.revokedOn}
                    onChange={(e) => updateAddFormField('revokedOn', e.target.value)}
                    className="h-8 text-xs"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={handleAddCancel}>
              Cancel
            </Button>
            <Button onClick={handleAddSave} disabled={!addFormData.reason || !addFormData.note || !addFormData.noticeGivenOn || !addFormData.lastWorkingDay}>
              {localIsEditing ? 'Update' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Internship/Probation Section
export const InternshipSection = ({ internshipData, setInternshipData }: InternshipSectionProps) => {
  const { hasPermission } = useAuthStore();
  
  // Check if user has permission to edit internship information
  const canEditInternship = hasPermission('EMPLOYEES_EDIT') || hasPermission('EMPLOYEES_CREATE');
  
  const [localIsEditing, setLocalIsEditing] = React.useState(false);
  const [localData, setLocalData] = React.useState<InternshipInfo[]>(() => {
    return Array.isArray(internshipData) ? internshipData : (internshipData ? [internshipData] : []);
  });
  const [_isAdding, setIsAdding] = React.useState(false);
  const [_, setDropdownOptions] = React.useState<{
  }>({
  });

  React.useEffect(() => {
    // Ensure internshipData is always an array
    const dataArray = Array.isArray(internshipData) ? internshipData : (internshipData ? [internshipData] : []);
    setLocalData(dataArray);
  }, [internshipData]);

  // Fetch dropdown options from backend
  React.useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const options = await getEmployeeDropdownOptions();
        setDropdownOptions(options);
      } catch (error) {
        console.error('Error fetching dropdown options:', error);
        // Fallback to default options
        setDropdownOptions({
        });
      }
    };

    fetchDropdownOptions();
  }, []);

  const handleSave = () => {
    setInternshipData(localData);
    setLocalIsEditing(false);
    setIsAdding(false);
    toast.success('Internship/Probation information updated successfully');
  };

  const handleCancel = () => {
    setLocalData(internshipData);
    setLocalIsEditing(false);
    setIsAdding(false);
    toast.info('Edit cancelled');
  };

  const handleEdit = () => {
    setLocalIsEditing(true);
    setIsAdding(false);
    toast.info('Edit mode enabled');
  };

  const handleAdd = () => {
    setIsAdding(true);
    setLocalIsEditing(true);
    const newInternship: InternshipInfo = {
      startDate: '',
      endDate: '',
      length: '',
      reduceExtended: ''
    };
    setLocalData([...localData, newInternship]);
    toast.info('Add new internship');
  };

  const handleDelete = (index: number) => {
    const updatedData = localData.filter((_, i) => i !== index);
    setLocalData(updatedData);
    toast.success('Internship deleted successfully');
  };

  const updateField = (index: number, field: keyof InternshipInfo, value: string) => {
    const updatedData = [...localData];
    updatedData[index] = {
      ...updatedData[index],
      [field]: value
    };
    setLocalData(updatedData);
  };

  return (
    <Card className="border border-gray-200 shadow-sm bg-white rounded-lg">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <Award className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-base font-semibold text-gray-900">
                Internship/Probation
              </CardTitle>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {canEditInternship && localIsEditing && (
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                className="gap-1 bg-blue-600 hover:bg-blue-700 text-xs px-3 py-1"
              >
                <Save className="h-3 w-3" />
                Save
              </Button>
            )}
            {canEditInternship && !localIsEditing && (
              <Button
                variant="default"
                size="sm"
                onClick={handleAdd}
                className="gap-1 bg-green-600 hover:bg-green-700 text-xs px-3 py-1"
              >
                <Plus className="h-3 w-3" />
                Add New
              </Button>
            )}
            {canEditInternship && (
            <Button
              variant="outline"
              size="sm"
              onClick={localIsEditing ? handleCancel : handleEdit}
              className="gap-1 hover:bg-gray-50 text-xs px-3 py-1"
            >
              {localIsEditing ? (
                <>
                  <X className="h-3 w-3" />
                  Cancel
                </>
              ) : (
                <>
                  <Edit2 className="h-3 w-3" />
                  Edit
                </>
              )}
            </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
          {localIsEditing ? (
          <div className="space-y-4">
            {Array.isArray(localData) && localData.map((internship, index) => (
              <div key={index} className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-700">Internship {index + 1}</h4>
                  {localData.length > 1 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(index)}
                      className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50 text-xs px-2 py-1"
                    >
                      <Trash2 className="h-3 w-3" />
                      Delete
                    </Button>
                  )}
                </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700">Start Date</Label>
                <Input
                  type="date"
                      value={internship.startDate}
                      onChange={(e) => updateField(index, 'startDate', e.target.value)}
                  className="h-8 text-xs"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700">End Date</Label>
                <Input
                  type="date"
                      value={internship.endDate}
                      onChange={(e) => updateField(index, 'endDate', e.target.value)}
                  className="h-8 text-xs"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700">Length</Label>
                <Input
                  type="number"
                      value={internship.length}
                      onChange={(e) => updateField(index, 'length', e.target.value)}
                  placeholder="Enter length in months"
                  className="h-8 text-xs"
                />
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs font-medium text-gray-700">Reduce/Extended</Label>
                <Select 
                      value={internship.reduceExtended || ''} 
                      onValueChange={(value) => updateField(index, 'reduceExtended', value === 'clear' ? '' : value)}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Select option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="clear">
                      <span className="text-gray-500 italic">Clear selection</span>
                    </SelectItem>
                    <SelectItem value="Reduced">Reduced</SelectItem>
                    <SelectItem value="Extended">Extended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
                </div>
              </div>
            ))}
            </div>
          ) : (
            <div className="space-y-3">
            {Array.isArray(localData) && localData.length > 0 ? (
              localData.map((internship, index) => (
                <div
                  key={index}
                  className="bg-gray-50 rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
                >
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-blue-50 rounded-lg flex items-center justify-center">
                          <Award className="h-3 w-3 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-medium text-sm text-gray-900">
                            Internship {index + 1}
                          </div>
                          <div className="text-xs text-gray-500">
                            Internship Information
                          </div>
                        </div>
                      </div>
                      <Badge 
                        variant="secondary" 
                        className="text-xs bg-blue-50 text-blue-600"
                      >
                        Intern
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1 border-t border-gray-200">
                      {internship.startDate && (
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-blue-600" />
                          <span className="text-xs text-gray-600">Start: {formatDate(internship.startDate)}</span>
                        </div>
                      )}
                      {internship.endDate && (
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-blue-600" />
                          <span className="text-xs text-gray-600">End: {formatDate(internship.endDate)}</span>
                        </div>
                      )}
                      {internship.length && (
                        <div className="flex items-center gap-1">
                          <Award className="h-3 w-3 text-blue-600" />
                          <span className="text-xs text-gray-600">Length: {internship.length} months</span>
                        </div>
                      )}
                      {internship.reduceExtended && (
                        <div className="flex items-center gap-1">
                          <Award className="h-3 w-3 text-blue-600" />
                          <span className="text-xs text-gray-600">Status: {internship.reduceExtended}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12 text-gray-500">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">No Internship Records</h3>
                <p className="text-sm text-gray-500 mb-4">No internship information has been recorded for this employee.</p>
                {canEditInternship && (
                  <div className="flex flex-col items-center gap-2">
                    <p className="text-xs text-gray-400">Click "Add New" to create a new internship record</p>
                    <div className="w-32 h-1 bg-gray-200 rounded-full"></div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}; 